import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useAuth } from "shared/contexts/AuthProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import { userDataReducer, initialUserData} from "shared/reducer/userDataReducer";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { servicesUrlConfig } from "shared/config/setting";
import { robotsService } from "features/administration/services";

const UserDataContext = createContext();

export function UserProvider({ children }) {

  const { userOrgs, orgsLoading } = useUserOrg();

  const [robotGroupsLoading, setRobotGroupsLoading] = useState(false);
  const [robotConnectionsLoading, setRobotConnectionsLoading] = useState(false);
  const [error, setError] = useState("");
  const [userDataState, dispatch] = useReducer(
    userDataReducer,
    initialUserData
  );

  const { notifyError } = useNotification();

  const { auth, loading, user, isAuthenticated, authFetch } = useAuth();

  const history = useHistory();
  const location = useLocation();

  const getRobotList = async () => {
    if (isAuthenticated) {
      setRobotGroupsLoading(true);
      setRobotConnectionsLoading(true);
      // Fetch the robot list for the authenticated user
      robotsService.getMyRobots(authFetch)
        .then(async (response) => {
          const data = response.data;
          // console.log("Fetching user's robots data done ...", data);
          dispatch({ type: "SET_USER_ROBOTS_DATA", payload: data });
        })
        .catch((err) => {
          const errorMessage = err.response?.data?.message || err.message;
          console.error("Error fetching user's robots data", err);
          setError(errorMessage || "Failed to fetch user data");
          notifyError("Failed to fetch user data: " + errorMessage);
        })
        .finally(() => {
          setRobotGroupsLoading(false);
        });

      const robotConnsURL = servicesUrlConfig.ROBOTS_CONNECTIONS_API.get_by_user;
      // const robotConnsURL = getFetchURL("/api/user/robots");
      authFetch(robotConnsURL)
        .then(async (response) => {
          const data = response.data;
          // console.log("Fetching robot channels list done ...", data);
          dispatch({ type: "SET_ROBOTLIST", payload: data });
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message || error.message;
          console.error("Error fetching robot channels list", error);
          setError(errorMessage || "Failed to fetch robot list");
          notifyError("Failed to fetch robot list: " + errorMessage);
        })
        .finally(() => {
          setRobotConnectionsLoading(false);
        });
    } else {
      dispatch({ type: "SET_USER_ROBOTS_DATA", payload: null });
      dispatch({ type: "SET_ROBOTLIST", payload: [] });
      // redirectToLoginPage(location);
    }
  };
  
  const isRobotOnline = (robot) => {
    const found = userDataState.robotList.find((rb) => rb.id === robot.id);
    return found && robot.status == "online";
  };

  const hasCamera = (robot) => {
    const settings = robot?.setting || {};
    if (!settings?.cameras) return false;
    const cameras = settings?.cameras || [];
    for (let index = 0; index < cameras.length; index++) {
      const camera = cameras[index];
      if (camera?.class_name != "DummyCamera") {
        return true;
      }
    }
    return false;
  };

  const getRobotClientCamerasSetting = (robot) => {
    const settings = robot?.setting || {};
    if (!settings?.cameras) return [];
    const cameras = settings?.cameras || [];
    return cameras;
  };

  const getRobotClientSpeakersSetting = (robot) => {
    const settings = robot?.setting || {};
    if (!settings?.speakers) return [];
    const speakers = settings?.speakers || [];
    return speakers;
  };

  const getRobotClientMicrophonesSetting = (robot) => {
    const settings = robot?.setting || {};
    if (!settings?.microphones) return [];
    const microphones = settings?.microphones || [];
    return microphones;
  };

  const getCameraPeers = (robotGroup) => {
    return robotGroup?.connections?.filter((robot) => hasCamera(robot));
  };

  const getConnectingUserClientByRole = (robotGroup, roleName) => {
    return (
      robotGroup?.connecting_user_clients
        ?.filter(([userClient, { state, role }]) => {
          return role == roleName;
        })
        .map(([userClient, { state, role }]) => {
          return {
            userClient,
            state,
            role,
          };
        }) || []
    );
  };

  const getConnectingUserClientByState = (robotGroup, stateName) => {
    return (
      robotGroup?.connecting_user_clients
        ?.filter(([userClient, { state, role }]) => {
          return state == stateName;
        })
        .map(([userClient, { state, role }]) => {
          return {
            userClient,
            state,
            role,
          };
        }) || []
    );
  };

  useEffect(() => {
    if (loading) return;
    getRobotList();
  }, [auth]);

  const robotProfiles = userDataState.robotsData?.results || [];

  const robotListLoading =
    robotConnectionsLoading || robotGroupsLoading || orgsLoading;

  return (
    <UserDataContext.Provider
      value={{
        userDataState,
        error,
        dispatch,
        isRobotOnline,
        getCameraPeers,
        getConnectingUserClientByRole,
        getConnectingUserClientByState,
        robotListLoading,
        robotList: userDataState.robotList,
        orgsRoles: userOrgs,
        robotProfiles,
        robotGroups: userDataState.robotGroups,
        robotsData: userDataState.robotsData,
        orgsData: userDataState.orgsData,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
}

export const useUserData = () => useContext(UserDataContext);
